/**
 * Типы предметной области экономики УЛЬЯ.
 *
 * Назначение файла: описать данные, на которых работают формулы баланса, так чтобы
 * ошибку нельзя было сделать молча. Отдельные типы для валюты (milli) и для качества
 * Следа существуют именно потому, что смешать «☾» и «milli» — это ошибка на три порядка.
 *
 * Импортов нет намеренно: типы не должны тянуть за собой ни БД, ни сеть, ни React
 * (docs/tech/01-architecture.md — правила зависимостей пакетов).
 *
 * Источник: docs/economy/06-formulas.md, docs/tech/03-data-model.md
 */

// ─────────────────────────────────────────────────────────────────────────────
// Валюта
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Сумма во внутренних единицах: 1 ☾ = 1000 milli.
 * Всегда целое число. Внутри сервера все расчёты идут в milli, чтобы избежать
 * плавающей запятой в деньгах (Э6.1).
 */
export type Milli = number;

/** Сумма в кристаллах, как её видит игрок. Используется только в константах и в интерфейсе. */
export type Crystals = number;

/** 1 ☾ в milli. */
export const MILLI_PER_CRYSTAL = 1000;

/** Перевод кристаллов в milli. Вход обязан быть неотрицательным. */
export function crystalsToMilli(value: Crystals): Milli {
  return Math.round(value * MILLI_PER_CRYSTAL);
}

/**
 * Перевод milli в целые кристаллы для зачисления игроку.
 * Округление всегда вниз: разница остаётся в казне соты или уходит в burn (Э6.1).
 */
export function milliToCrystalsFloor(value: Milli): Crystals {
  return Math.floor(value / MILLI_PER_CRYSTAL);
}

// ─────────────────────────────────────────────────────────────────────────────
// Соты
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Тип соты по классификатору OSM (docs/04-map-and-geo.md).
 * От типа зависят доход, ставка дани и ставка осады.
 * `sanctuary` (заповедная: школы, больницы) не даёт дохода и не воюет.
 */
export type HexKind =
  | "ordinary" // обычная: двор, застройка
  | "grove" // роща: парк, зелень — бесплатный проход
  | "academy" // академия: университет, библиотека
  | "shrine" // святилище: памятник, храм
  | "market" // рынок: торговля
  | "crossroad" // перекрёсток: транспортный узел
  | "sanctuary"; // заповедная: школа, больница — вне боёв и экономики

/** Качество Следа по протоколу GPS (docs/05-traces.md). Влияет на дань и опыт. */
export type TraceQuality = "echo" | "true" | "deep" | "anchor";

/** Отношение гостя к владельцу соты. Влияет на множитель дани. */
export type Relation = "self" | "ally" | "neutral" | "enemy";

/** Ступень долга игрока (Э6.7). */
export type DebtTier = "none" | "arrears" | "shadow" | "arrest";

/** Вид цели осады: определяет ставку (Э6.6). */
export type SiegeTarget =
  | "empty"
  | "contestedEmpty"
  | "ordinary"
  | "grove"
  | "academy"
  | "market"
  | "crossroad"
  | "shrine"
  | "hearthWeakening"
  | "clanHq";

/** Исход осады. */
export type SiegeOutcome = "attackerWin" | "defenderWin" | "draw" | "cancelled";

// ─────────────────────────────────────────────────────────────────────────────
// Состояния, от которых считается баланс
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Кошелёк игрока (docs/economy/01-currencies.md).
 * `hive` может уйти в 0, но не ниже: минус выражается через `debt`.
 * `cash` неприкосновенны: их не списывает ни дань, ни пеня.
 * `held` — сумма, зарезервированная под объявленные осады.
 */
export interface Wallet {
  /** Игровые кристаллы ☾, milli. Инвариант: >= 0. */
  readonly hive: Milli;
  /** Купленные кристаллы ✦, milli. Инвариант: >= 0, автоматически не списываются. */
  readonly cash: Milli;
  /** Долг, milli. Инвариант: 0 <= debt <= лимит долга. */
  readonly debt: Milli;
  /** Заморожено под ставки осад, milli. Инвариант: 0 <= held <= hive. */
  readonly held: Milli;
}

/** Данные игрока, влияющие на расчёты. Только то, что нужно формулам. */
export interface PlayerState {
  readonly level: number;
  readonly wallet: Wallet;
  /** Суток без входа в игру: гасит доход сот (Э6.3, kАктивность). */
  readonly daysWithoutLogin: number;
  /** Личных сот во владении. Форпост считается за 0.5 (Э6.3, штраф растянутости). */
  readonly personalHexes: number;
  readonly underNewbieShield: boolean;
  readonly accountAgeHours: number;
  /** Заходил ли в текущие экономические сутки: без входа Очаг не платит (Э6.4). */
  readonly loggedInToday: boolean;
  readonly hearthInGrove: boolean;
  /** Земля под Очагом принадлежит игроку или его клану. */
  readonly landUnderHearthOwned: boolean;
  /** Объявленных осад за текущие экономические сутки: множитель навязчивости (Э6.6). */
  readonly siegesToday: number;
}

/** Состояние соты для расчёта дохода и дани. */
export interface HexState {
  readonly kind: HexKind;
  /** Связана ли цепочкой владений с Очагом: разрыв режет доход до 30% (Э6.3). */
  readonly connectedToHearth: boolean;
  /** Уровень укрепления 0..3. */
  readonly fortification: number;
  /** Чужих активных Очагов внутри соты: жильцы платят оброк и слегка растят доход. */
  readonly foreignActiveHearths: number;
  /** Клановая сота: держателю идёт 30%, остальное в склад. */
  readonly clanOwned: boolean;
  /** Форпост — сота без связи с основным массивом: половинная дань. */
  readonly outpost: boolean;
  /** Сота «ранена» после недавнего боя: доход вполовину. */
  readonly wounded: boolean;
  /** Казна соты, milli. Становится трофеем при захвате. */
  readonly vault: Milli;
}
